import { getRailsClient } from "sj-ui-toolkit/src/client";
import type { ToastMessage } from "primereact/toast";

type ShowToastFn = (message: ToastMessage) => void;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PathData {
  id?: string | number;
  roleGroupId?: string | number;
  name: string;
  description: string;
  levelId?: string | number;
  levelName?: string;
  rankName?: string;
  pathSource?: string;
  pathType?: string | null;
  isSjContent?: boolean;
  type?: string | null;
  activated?: boolean;
  archived?: boolean;
  restricted?: boolean;
  totalPoints?: number;
  lessonsCount?: number;
  totalDuration?: number;
  difficulty?: string;
  versions?: Array<{ lessons?: LessonSummary[]; updatedAt?: string }>;
  lessons?: LessonSummary[];
  audiences?: Array<{ value: string }>;
  retired?: boolean;
  contentType?: string;
  levelType?: string;
  reassign?: boolean;
  includeCustomCertDescription?: boolean;
}

export interface LessonSummary {
  id: string | number;
  name: string;
  description?: string;
  durationNumber?: number;
  duration?: number;
  required?: boolean;
  status?: string;
  lessonKey?: string;
  points?: number;
  contentType?: string;
  lessonTypeFilter?: string;
}

export interface PathsListResponse {
  defaultPaths?: PathData[];
  paths?: PathData[];
  quests?: PathData[];
}

export interface FilteredPathsResponse {
  paths?: PathData[];
}

export interface PathDetailResponse {
  role: PathData;
}

interface LoadFilteredPathsParams {
  path_type?: "path" | "quest";
  path_source?: "custom" | "default";
  content_type?: string;
  withDuration?: boolean;
  [key: string]: unknown;
}

interface ToggleActivatedOptions {
  id: string | number;
  paths: PathData[];
  type: string | null;
  setData: React.Dispatch<
    React.SetStateAction<{
      customPaths?: PathData[];
      defaultPaths?: PathData[];
      quests?: PathData[];
    }>
  >;
  onSuccess?: () => void;
  onRevert?: () => void;
  showToast: ShowToastFn;
}

interface FormatPathDataOptions {
  reassign?: boolean;
  name?: string;
  restricted?: boolean;
}

// ---------------------------------------------------------------------------
// HTTP API functions
// ---------------------------------------------------------------------------

export const loadPathsAsync = async (
  params: Record<string, unknown> = {}
): Promise<PathsListResponse> => {
  const API = `/api/internal_api/v1/admin/roles`;
  const { data } = await getRailsClient().get<PathsListResponse>(API, { params });
  return data;
};

export const loadFilteredPathsAsync = async (
  params: LoadFilteredPathsParams
): Promise<FilteredPathsResponse> => {
  const API = `/api/internal_api/atlas/v1/admin/roles`;
  const { data } = await getRailsClient().get<FilteredPathsResponse>(API, { params });
  return data;
};

export const loadPathByIdAsync = async (id: string | number): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles/${id}`;
  const { data } = await getRailsClient().get<PathDetailResponse>(API);
  return data;
};

export const createPathAsync = async (
  pathData: Record<string, unknown>
): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles`;
  const { data } = await getRailsClient().post<PathDetailResponse>(API, pathData);
  return data;
};

export const updatePathAsync = async (
  id: string | number,
  pathData: Record<string, unknown>
): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles/${id}`;
  const { data } = await getRailsClient().put<PathDetailResponse>(API, pathData);
  return data;
};

export const archivePathAsync = async ({
  id,
  archived,
  activated,
  lessonGroupIds,
  requiredLessonIds,
}: {
  id: string | number;
  archived?: boolean;
  activated?: boolean;
  lessonGroupIds: (string | number)[];
  requiredLessonIds: (string | number)[];
}): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles/${id}`;
  const payload: Record<string, unknown> = {
    lesson_group_ids: lessonGroupIds.join(","),
    required_lesson_ids: requiredLessonIds.join(","),
  };
  if (archived !== undefined) payload.archived = archived;
  if (activated !== undefined) payload.activated = activated;
  const { data } = await getRailsClient().put<PathDetailResponse>(API, payload);
  return data;
};

export const updateManyPathsAsync = async ({
  ids,
  archived,
  activated,
}: {
  ids: (string | number)[];
  archived?: boolean;
  activated?: boolean;
}): Promise<void> => {
  const API = `/api/internal_api/v2/admin/roles/update_many`;
  const payload: Record<string, unknown> = { ids };
  if (archived !== undefined) payload.archived = archived;
  if (activated !== undefined) payload.activated = activated;
  await getRailsClient().put(API, payload);
};

export const restrictPathAsync = async ({
  id,
  restricted,
  lessonGroupIds,
  requiredLessonIds,
}: {
  id: string | number;
  restricted: boolean;
  lessonGroupIds: string | (string | number)[];
  requiredLessonIds: string | (string | number)[];
}): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles/${id}`;
  const { data } = await getRailsClient().put<PathDetailResponse>(API, {
    restricted,
    lesson_group_ids: lessonGroupIds,
    required_lesson_ids: requiredLessonIds,
  });
  return data;
};

export const duplicatePathAsync = async (
  pathData: Record<string, unknown>
): Promise<PathDetailResponse> => {
  const API = `/api/internal_api/v1/admin/roles`;
  const { data } = await getRailsClient().post<PathDetailResponse>(API, pathData);
  return data;
};

export const deletePathAsync = async (pathDetails: {
  roleGroupId?: string | number;
  id?: string | number;
}): Promise<void> => {
  const deleteId = pathDetails.roleGroupId ?? pathDetails.id;
  const API = `/api/internal_api/v1/admin/roles/${deleteId}`;
  await getRailsClient().delete(API);
};

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

export const getPathType = (
  pathDetails?: Partial<PathData> | null
): "Path" | "Quest" | "unknown" => {
  if (!pathDetails) return "unknown";

  const { pathSource, pathType } = pathDetails;

  if (pathSource && typeof pathType === "string") {
    if (pathSource === "default") return "Path";
    if (pathSource === "custom" && pathType === "course") return "Path";
    if (pathSource === "custom" && pathType === "quest") return "Quest";
  }

  const { isSjContent, type } = pathDetails;

  if (isSjContent) return "Path";
  if (type === "my_paths") return "Path";
  if (type === "activities") return "Path";
  if (type === null) return "Quest";

  if (pathSource === "custom" && !pathType) return "Path";
  if (pathSource === "custom" && pathType === null) return "Quest";

  return "unknown";
};

export const getPathsListRoute = (pathDetails?: Partial<PathData> | null): string => {
  const PATHS_RECOMMENDED_PATH = "/admin/paths/recommended";
  const PATHS_CUSTOM_PATH = "/admin/paths/custom";
  const PATHS_QUESTS_PATH = "/admin/paths/quests";

  if (!pathDetails) return PATHS_RECOMMENDED_PATH;

  const { isSjContent, type, pathSource, pathType } = pathDetails;

  if (isSjContent || pathSource === "default") return PATHS_RECOMMENDED_PATH;
  if (type === null || pathType === "quest") return PATHS_QUESTS_PATH;

  return PATHS_CUSTOM_PATH;
};

export const getActivityPathConflict = (
  rankName: string | undefined,
  allPaths: Array<{ rankName: string; activated: boolean; id: string | number; name: string }>
) => {
  if (!rankName || !["brown", "black"].includes(rankName)) return null;
  return allPaths.find(p => p.rankName === rankName && p.activated) ?? null;
};

// ---------------------------------------------------------------------------
// Business logic helpers
// ---------------------------------------------------------------------------

export const toggleActivated = async ({
  id,
  paths,
  type,
  setData,
  onSuccess,
  onRevert,
  showToast,
}: ToggleActivatedOptions): Promise<void> => {
  const currentItem = paths.find(item => String(item.id) === String(id));
  if (!currentItem) return;

  const pathKey = type === "my_paths" ? "customPaths" : type === null ? "quests" : "defaultPaths";

  let newActivated: boolean;
  let newArchived: boolean;
  if (currentItem.activated && currentItem.archived) {
    newActivated = false;
    newArchived = true;
  } else if (!currentItem.activated && !currentItem.archived) {
    newActivated = true;
    newArchived = false;
  } else {
    newActivated = !currentItem.activated;
    newArchived = !currentItem.archived;
  }

  setData(prev => ({
    ...prev,
    [pathKey]:
      (prev[pathKey as keyof typeof prev] as PathData[] | undefined)?.map(item =>
        String(item.id) === String(id)
          ? { ...item, activated: newActivated, archived: newArchived }
          : item
      ) ?? [],
  }));

  try {
    await updateManyPathsAsync({ ids: [id], archived: newArchived, activated: newActivated });
    showToast({
      severity: "success",
      detail: `Path ${newActivated ? "activated" : "deactivated"} successfully`,
    });
    onSuccess?.();
  } catch (error) {
    console.error("Error toggling path activation:", error);
    setData(prev => ({
      ...prev,
      [pathKey]:
        (prev[pathKey as keyof typeof prev] as PathData[] | undefined)?.map(item =>
          String(item.id) === String(id)
            ? {
                ...item,
                activated: currentItem.activated ?? false,
                archived: currentItem.archived ?? false,
              }
            : item
        ) ?? [],
    }));
    showToast({ severity: "error", detail: "Failed to update path activation" });
    onRevert?.();
  }
};

export const clonePath = async (
  path: PathData,
  reassign: boolean,
  uniqueName: string | null,
  showToast: ShowToastFn
): Promise<PathDetailResponse> => {
  let pathData = path;

  if (!path.versions?.[0]?.lessons) {
    const response = await loadPathByIdAsync(path.id!);
    pathData = response.role;
  }

  const pathName = uniqueName ?? pathData.name;
  const formattedData = formatPathDataForAPI(pathData, { reassign, name: pathName });

  try {
    const response = await duplicatePathAsync(formattedData);
    showToast({ severity: "success", detail: "Path cloned successfully" });
    return response;
  } catch (error) {
    console.error("Error cloning path:", error);
    showToast({ severity: "error", detail: "Failed to clone path" });
    throw error;
  }
};

export const deletePath = async (
  pathDetails: PathData,
  navigate: (path: string) => void,
  showToast: ShowToastFn
): Promise<void> => {
  try {
    await deletePathAsync(pathDetails as { id: string | number });
    showToast({ severity: "success", detail: "Path deleted successfully" });
    navigate("/admin/paths/custom");
  } catch (error) {
    console.error("Error deleting path:", error);
    const errorMessage =
      (error as { response?: { data?: { errors?: string; error?: string } } })?.response?.data
        ?.errors ??
      (error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
      "Failed to delete path";
    showToast({ severity: "error", detail: errorMessage });
    throw error;
  }
};

export const toggleRestrictAsync = async (
  pathDetails: PathData,
  showToast: ShowToastFn,
  options: { restricted?: boolean } = {}
): Promise<void> => {
  const lessons = pathDetails.versions?.[0]?.lessons ?? pathDetails.lessons ?? [];
  const lessonGroupIds = lessons.map(l => l.id).join(",");
  const requiredLessonIds = lessons
    .filter(l => l.required)
    .map(l => l.id)
    .join(",");
  const newRestricted =
    options.restricted !== undefined ? options.restricted : !pathDetails.restricted;

  await restrictPathAsync({
    id: pathDetails.id!,
    restricted: newRestricted,
    lessonGroupIds,
    requiredLessonIds,
  });

  showToast({
    severity: "success",
    detail: `Path ${newRestricted ? "restricted" : "unrestricted"} successfully`,
  });
};

export const copyLinkToClipboard = (
  pathId: string | number | undefined,
  showToast?: ShowToastFn
) => {
  if (!pathId) return;
  navigator.clipboard
    .writeText(`${window.location.origin}/path/${pathId}`)
    .then(() => {
      showToast?.({
        severity: "success",
        summary: "Link Copied",
        detail: "Path link has been copied to clipboard",
        life: 3000,
      });
    })
    .catch(() => {
      showToast?.({
        severity: "error",
        summary: "Copy Failed",
        detail: "Unable to copy link to clipboard",
        life: 3000,
      });
    });
};

export const formatPathDataForAPI = (
  pathData: Partial<PathData> & {
    lessonGroupIds?: (string | number)[];
    requiredLessonIds?: (string | number)[];
    levelType?: string;
    reassign?: boolean;
    requireRetake?: boolean;
    includeCustomCertDescription?: boolean;
  },
  options: FormatPathDataOptions = {}
): Record<string, unknown> => {
  let lessonGroupIds: (string | number)[] = [];
  let requiredLessonIds: (string | number)[] = [];

  if (pathData.lessonGroupIds) {
    lessonGroupIds = pathData.lessonGroupIds;
    requiredLessonIds = pathData.requiredLessonIds ?? [];
  } else if (pathData.versions?.[0]?.lessons ?? pathData.lessons) {
    const lessons = pathData.versions?.[0]?.lessons ?? pathData.lessons ?? [];
    lessonGroupIds = lessons.map(l => l.id);
    requiredLessonIds = lessons.filter(l => l.required).map(l => l.id);
  }

  const apiData: Record<string, unknown> = {
    name: options.name ?? pathData.name,
    description: pathData.description,
    level_id: String(pathData.levelId ?? ""),
    levelType: pathData.levelType ?? "course",
    total_points: String(pathData.totalPoints ?? 0),
    reassign:
      options.reassign !== undefined
        ? options.reassign
        : (pathData.reassign ?? pathData.requireRetake ?? false),
    includeCustomCertDescription: pathData.includeCustomCertDescription ?? false,
    thumbImageUrl: "",
    lesson_group_ids: lessonGroupIds.join(","),
    required_lesson_ids: requiredLessonIds.join(","),
    activated: pathData.activated ?? false,
    archived: pathData.archived ?? false,
  };

  if (options.restricted !== undefined) {
    apiData.restricted = options.restricted;
  }

  return apiData;
};

// ---------------------------------------------------------------------------
// Name / validation utilities
// ---------------------------------------------------------------------------

export const generateUniqueName = (
  baseName: string,
  existingNames: string[],
  maxLength?: number
): string => {
  if (!baseName || !existingNames || existingNames.length === 0) {
    return maxLength ? baseName.slice(0, maxLength) : baseName;
  }

  const normalizedExisting = existingNames.map(n => n.toLowerCase().trim());
  const baseNameLower = baseName.toLowerCase().trim();

  if (!normalizedExisting.includes(baseNameLower)) {
    return maxLength ? baseName.slice(0, maxLength) : baseName;
  }

  const baseNamePattern = /^(.+?)\s*\[\d+\]\s*$/;
  const baseNameMatch = baseNameLower.match(baseNamePattern);
  const actualBaseName = baseNameMatch ? baseNameMatch[1]!.trim() : baseNameLower;

  let maxIncrement = 0;
  const bracketPattern = new RegExp(
    `^${actualBaseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*\\[(\\d+)\\]$`
  );

  normalizedExisting.forEach(n => {
    const match = n.match(bracketPattern);
    if (match) {
      const increment = parseInt(match[1]!, 10);
      if (increment > maxIncrement) maxIncrement = increment;
    }
  });

  const nextIncrement = maxIncrement + 1;
  const uniqueName = `${actualBaseName} [${nextIncrement}]`;
  return maxLength ? uniqueName.slice(0, maxLength) : uniqueName;
};

export const validatePathName = (
  name: string,
  allPathNames: string[],
  originalPathName?: string | null,
  contentTypeLabel = "Path"
): { isValid: boolean; message: string; isDuplicate: boolean; suggestion?: string | null } => {
  const trimmedName = name.trim();
  const newNameLower = trimmedName.toLowerCase();
  const originalNameLower = originalPathName ? originalPathName.trim().toLowerCase() : null;
  const hasNameChanged = newNameLower !== originalNameLower;
  const isDuplicate =
    hasNameChanged && allPathNames.some(n => n.trim().toLowerCase() === newNameLower);

  const isTooLong = trimmedName.length > 100;
  const isEmpty = trimmedName.length === 0;

  let message = "";
  if (isTooLong) {
    message = `${contentTypeLabel} name must be 100 characters or less`;
  } else if (isDuplicate) {
    message = `A ${contentTypeLabel.toLowerCase()} with this name already exists. Please choose a different name.`;
  } else if (isEmpty) {
    message = `${contentTypeLabel} name is required`;
  }

  const result = {
    isValid: !isDuplicate && !isTooLong && !isEmpty,
    message,
    isDuplicate,
    suggestion: null as string | null,
  };

  if (isDuplicate && !isTooLong) {
    const suggestion = generateUniqueName(name, allPathNames);
    result.suggestion = suggestion;
    result.message = `A ${contentTypeLabel.toLowerCase()} with this name already exists. Try: "${suggestion}"`;
  }

  return result;
};

export const validatePathDescription = (
  description: string | undefined,
  contentTypeLabel = "Path"
): { isValid: boolean; message: string } => {
  const trimmed = description ? description.trim() : "";
  const isTooLong = trimmed.length > 500;
  const isEmpty = trimmed.length === 0;

  let message = "";
  if (isTooLong) {
    message = `${contentTypeLabel} description must be 500 characters or less`;
  } else if (isEmpty) {
    message = `${contentTypeLabel} description is required`;
  }

  return { isValid: !isTooLong && !isEmpty, message };
};
